import {
  Button,
  Center,
  HStack,
  Icon,
  Image,
  Skeleton,
  Stack,
  Text,
  useClipboard,
  VStack,
} from '@chakra-ui/react';
import { getAssetUrl } from '@cronos-app/chakra';
import { CollapsedText, Quantity } from '@cronos-app/components';
import { ChevronRight, Copy, Tick } from '@cronos-app/icons';
import { currentWallet } from '@cronos-app/wallet';
import MobileSupportTooltip from '@ui/components/MobileSupportTooltip';
import { useExtendedTokenInfo } from '@ui/views/card-cronos/core/configs';
import { TopUpTab } from '@ui/views/card-cronos/core/types';
import type { TopUpRecord } from '@ui/views/card-cronos/core/utils';
import {
  useCdcCardView,
  useTopUpHistoryDepositAddresses,
  useTopUpHistoryList,
} from '@ui/views/card-cronos/core/utils';
import { format } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { DepositAddressSelector } from './DepositAddressSelector';

export const TopUpHistory = () => {
  const t = useTranslations();
  const { data: allDepositAddresses } = useTopUpHistoryDepositAddresses();
  const [select, setSelect] = useState<string>('');
  const { list, hasNext, fetchMore, isLoading } = useTopUpHistoryList(
    select || undefined,
  );
  const { setTopUpTab } = useCdcCardView();
  if (!allDepositAddresses?.length)
    return (
      <VStack
        gap="4"
        mt={{
          base: 0,
          desktop: '80px',
        }}
        mb={{
          base: '40px',
          desktop: '120px',
        }}
      >
        <Text textStyle="body2">
          {t('card.You haven’t top up any tokens yet')}
        </Text>
        <Button
          variant="ghost"
          bgColor="#159DFF"
          rounded="4px"
          size="sm"
          onClick={() => setTopUpTab(TopUpTab.TopUp)}
          textTransform="unset"
        >
          {t('card.Top up now')}
        </Button>
      </VStack>
    );
  return (
    <>
      <DepositAddressSelector
        allDepositAddresses={allDepositAddresses}
        select={select}
        onSelect={setSelect}
      />
      <Stack gap="8" mt="5">
        {list.map((record) => (
          <RecordRenderer record={record} key={record.timestamp} />
        ))}
        {isLoading && <Loading />}
      </Stack>
      {hasNext && (
        <Center mt="5">
          <Button onClick={fetchMore} variant="link" isLoading={isLoading}>
            <Text textStyle="body2Bold" color="brand.secondary-sky-blue">
              {t('card.View More')}
            </Text>
            <Icon
              as={ChevronRight}
              boxSize="6"
              transform="rotate(90deg)"
              transition="transform 0.2s"
              color="brand.secondary-sky-blue"
            />
          </Button>
        </Center>
      )}
    </>
  );
};

const RecordRenderer = ({ record }: { record?: TopUpRecord }) => {
  const account = currentWallet.useAccount();
  const fromToken = useExtendedTokenInfo(record?.from);
  const toToken = useExtendedTokenInfo(record?.to);
  const { onCopy } = useClipboard(record?.depositAddress ?? '');
  return (
    <HStack justifyContent="space-between">
      {record ? (
        <Stack gap="1">
          <HStack gap="1">
            <Quantity value={record.fromAmount} />
            {fromToken?.Icon ? (
              <fromToken.Icon boxSize="4" />
            ) : (
              <Skeleton boxSize="4" />
            )}
          </HStack>
          <CollapsedText
            w={{
              base: '60px',
              desktop: '80px',
            }}
            remain={4}
            text={account ?? ''}
            textStyle="caption"
            color="brand.gray-scale-light-grey"
          />
        </Stack>
      ) : (
        <Skeleton h="6" w="20" />
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="29"
        height="2"
        viewBox="0 0 29 2"
        fill="none"
      >
        <path d="M0 1H29" stroke="url(#paint0_linear_6356_24572)" />
        <defs>
          <linearGradient
            id="paint0_linear_6356_24572"
            x1="15.021"
            y1="1.55357"
            x2="-6.60912e-07"
            y2="1.55355"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#34F792" />
            <stop offset="0.5" stopColor="#12CFF4" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {record ? (
        <VStack gap="1">
          <Icon as={Tick} boxSize="6" />
          <Text textStyle="caption" color="brand.gray-scale-light-grey">
            {format(new Date(record.timestamp), 'yyyy-MM-dd')}
          </Text>
        </VStack>
      ) : (
        <Skeleton h="6" w="20" />
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="8"
        viewBox="0 0 30 8"
        fill="none"
      >
        <path
          d="M29.3536 4.35355C29.5488 4.15829 29.5488 3.84171 29.3536 3.64645L26.1716 0.464466C25.9763 0.269204 25.6597 0.269204 25.4645 0.464466C25.2692 0.659728 25.2692 0.976311 25.4645 1.17157L28.2929 4L25.4645 6.82843C25.2692 7.02369 25.2692 7.34027 25.4645 7.53553C25.6597 7.7308 25.9763 7.7308 26.1716 7.53553L29.3536 4.35355ZM0 4.5H29V3.5H0V4.5Z"
          fill="url(#paint0_linear_6356_24576)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_6356_24576"
            x1="15.021"
            y1="4.55357"
            x2="-6.60912e-07"
            y2="4.55355"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#34F792" />
            <stop offset="0.5" stopColor="#12CFF4" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {record ? (
        <Stack alignItems="flex-end" gap="1">
          <HStack gap="1">
            <Quantity value={record.toAmount} />
            {toToken?.Icon ? (
              <toToken.Icon boxSize="4" />
            ) : (
              <Skeleton boxSize="4" />
            )}
          </HStack>
          <MobileSupportTooltip
            hasArrow
            label={record.depositAddress}
            placement="top-end"
          >
            <HStack gap="1" onClick={onCopy} cursor="pointer">
              <Image
                src={getAssetUrl('/images/cdc-app.svg')}
                boxSize="12px"
                alt="CDC App"
              />
              <CollapsedText
                w={{
                  base: '60px',
                  desktop: '80px',
                }}
                remain={4}
                text={record.depositAddress}
                textStyle="caption"
                color="brand.gray-scale-light-grey"
                textAlign="right"
              />
              <Icon as={Copy} boxSize="3" />
            </HStack>
          </MobileSupportTooltip>
        </Stack>
      ) : (
        <Skeleton h="6" w="20" />
      )}
    </HStack>
  );
};

const Loading = () => (
  <>
    {new Array(3).fill(0).map((_, index) => (
      <RecordRenderer key={index} />
    ))}
  </>
);
